import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';

import { BuildingBlock, BuildingBlockControlBaseComponent } from '../building-block.types';
import { PickList } from '../../../types/picklist';
import { PageInstance } from '@workflow/class/page.service';

/** This component dyanmically generates loan details. */
@BuildingBlock('dynamic-personal-info')
@Component({
  selector: 'ecp-dynamic-personal-info',
  templateUrl: './dynamic-personal-info.component.html',
  styleUrls: ['./dynamic-personal-info.component.scss'],
  standalone: false
})
export class DynamicPersonalInfoComponent extends BuildingBlockControlBaseComponent implements OnInit, OnDestroy {
  /**  idTypes */
  public idTypes: PickList[];
  /** Page as deduced from route */
  public page: PageInstance;
  /** formChangesSubscription */
  public formChangesSubscription: Subscription;
  /** storagePrefix */
  public storagePrefix: string;
  /** formGroup */
  public formGroup: UntypedFormGroup;
  /** isSubmitClicked */
  public isSubmitClicked: boolean;
  /** ID type dropdown Id */
  public idTypeFieldId: string;
  /** Loan Group */
  public groupId: string;
  /** Loan Product */
  public productId: string;

  public ngOnInit(): void {
    this.idTypes = [] as PickList[];
    this.page = this.workflowService.currentPage;
    this.storagePrefix = `ola:${this.page.pageIterationId >= 0 ? 'coBorrowers/' + this.page.pageIterationId.toString() + '/' : ''}`;
    this.idTypeFieldId = `${
      this.page.pageIterationId >= 0 ? 'ola-dynamic-personal-info-' + this.page.pageIterationId.toString() : 'ola-dynamic-personal-info'
    }`;
    this.buildForm();
    this.getLoanGroupProductId();
    this.getPickListItems(() => {
      if (!this.idTypes) {
        return;
      }
      this.hostPrefillService.fillIdentificationDetail();
    });
    this.resetDynamicFields();
    this.statefulService.wrap('ss:ola-submit-clicked').onChange(() => {
      this.isSubmitClicked = true;
    });
  }

  /**
   * method to get groupId and productId based on stateful values
   * @returns
   */
  private getLoanGroupProductId(): void {
    const loanGroupName = this.statefulService.wrap('ola:olaApplicationInformation/loanGroupName').valueString;
    const loanProductName: string = this.statefulService.wrap('ola:olaApplicationInformation/loanProductName').valueString;

    if (!loanGroupName && !loanProductName) {
      return;
    }

    if (
      this.adminPortalFeaturesService.config?.olaConfiguration &&
      this.adminPortalFeaturesService.config?.olaConfiguration?.consumerLoanGroups
    ) {
      const consumerLoanGroups = this.adminPortalFeaturesService.config?.olaConfiguration?.consumerLoanGroups;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const selectedLoanGroup: any = consumerLoanGroups.filter((t) => t.loanGroup.toLowerCase() === loanGroupName.toLowerCase())[0];
      if (selectedLoanGroup) {
        this.groupId = selectedLoanGroup.groupId;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const selectedLoanProduct: any = selectedLoanGroup.loanProducts.filter(
        (t) => !selectedLoanGroup.isLoanGroupHasProduct || t.productName === loanProductName
      )[0];
      if (selectedLoanProduct) {
        this.productId = selectedLoanProduct.productId;
      }
    }
  }

  /**
   * method to get identification picklist
   */
  private getPickListItems(cb: () => void): void {
    const identificationList = this.statefulService.wrap('mem:ola-identificationType-pick-list').valueString;
    if (identificationList) {
      this.idTypes = JSON.parse(identificationList)?.filter((t) => t.value.toLowerCase() === 'identificationtype')[0]?.pickListItems;
      setTimeout(() => {
        this.readValues();
      }, 100);
      cb();
    } else {
      this.l360Service.getProductIdentificationTypes(this.groupId, this.productId, (response) => {
        if (response?.length > 0) {
          const pickLists = response;
          this.statefulService.wrap('mem:ola-identificationType-pick-list').value = JSON.stringify(pickLists);
          this.idTypes = pickLists.filter((t) => t.value.toLowerCase() === 'identificationtype')[0]?.pickListItems;
          setTimeout(() => {
            this.readValues();
          }, 100);
          cb();
        }
      });
    }
  }

  public onChangeIdType(): void {
    const idTypeValue = this.formGroup.get('IdType').value;
    this.field.data.value = this.formGroup.valid ? idTypeValue : '';
    this.resetDynamicFields();
  }

  private resetDynamicFields(): void {
    if (this.page.pageIterationId >= 0) {
      this.statefulService.wrap(`ss:ola-dynamic-Personal-${this.page.pageIterationId}`).value = null;
    } else {
      this.statefulService.wrap('ss:ola-dynamic-Personal').value = null;
    }
  }

  private readValues(): void {
    this.setUIFields();
  }

  private setUIFields(): void {
    const idType = this.statefulService.wrap(this.storagePrefix + 'borrowerInfo/idType').valueString;
    const idTypeCount = this.idTypes?.filter((x) => x.value === idType)?.length;
    // eslint-disable-next-line @typescript-eslint/dot-notation
    if (idType && this.formGroup.controls['IdType'] && idTypeCount > 0) {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      this.formGroup.controls['IdType'].setValue(idType);
    } else {
      this.resetFields();
    }
  }

  private resetFields(): void {
    this.statefulService.wrap(this.storagePrefix + 'borrowerInfo/idType').value = null;
    this.statefulService.wrap(this.storagePrefix + 'borrowerInfo/idNumber').value = null;
    this.statefulService.wrap(this.storagePrefix + 'borrowerInfo/issuingState').value = null;
    this.statefulService.wrap(this.storagePrefix + 'borrowerInfo/issuedDate').value = null;
    this.statefulService.wrap(this.storagePrefix + 'borrowerInfo/expirationDate').value = null;
    this.statefulService.wrap(this.storagePrefix + 'borrowerInfo/issuingCountry').value = null;
    this.statefulService.wrap(this.storagePrefix + 'borrowerInfo/issuingInstitution').value = null;
  }
  private buildForm(): void {
    this.formGroup = this.formBuilder.group({
      IdType: this.formBuilder.control(null, [
        (s: UntypedFormControl) => {
          return this.customRequiredValidator(s);
        }
      ])
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private customRequiredValidator(c: UntypedFormControl): any {
    const isInvalid = c.value === undefined || c.value === null || c.value === '';
    return isInvalid
      ? {
          validateRequired: {
            valid: false
          },
          message: ''
        }
      : null;
  }

  public ngOnDestroy(): void {
    if (this.formChangesSubscription) {
      this.formChangesSubscription.unsubscribe();
    }
  }
}
